import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { EmailTemplateEditor } from "./EmailTemplateEditor";
import { EmailTemplate } from "../types";
import { axiosInstance } from "@/pages/auth/auth.context";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface EmailTemplateEditDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  template: EmailTemplate | null;
  onSave: (template: EmailTemplate) => void;
}

// Define variables for each template type
const templateVariables: Record<string, string[]> = {
  WELCOME: ["firstName", "lastName", "companyName"],
  EVENT_CREATED: ["address", "eventDate", "eventName", "startTime", "endTime"],
  EVENT_CANCELLED: ["eventDate", "eventName", "address"],
  NEW_BID_RECEIVED: [
    "companyName",
    "eventDate",
    "hcardDisplay",
    "vcardDownloadLink",
  ],
  NEW_BID_RECEIVED_CLIENT: ["eventDate"],
  EVENT_REMINDER: ["address", "eventDate", "startTime", "endTime"],
  ALERT: ["alertMessage"],
  ANNOUNCEMENT: ["announcementTitle", "announcementContent"],
  PASSWORD_RESET: ["firstName", "resetPasswordLink"],
};

export function EmailTemplateEditDialog({
  isOpen,
  onOpenChange,
  template,
  onSave,
}: EmailTemplateEditDialogProps) {
  const [editedSubject, setEditedSubject] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [editedSmsContent, setEditedSmsContent] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchTemplateDetails = async (id: number) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BASE_API_URL}notificationtemplates/${id}`,
      );
      const templateData = response.data;
      setEditedSubject(templateData.subject || "");

      // Update to handle the correct property name from backend
      setEditedContent(templateData.emailContent || templateData.content || "");
      setEditedSmsContent(templateData.smsContent || "");
    } catch (err) {
      console.error("Error fetching template details:", err);
      toast({
        title: "Error",
        description: "Could not load template details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (template) {
      // Initialize with the data we already have
      setEditedSubject(template.subject || "");

      // Account for both possible property names
      setEditedContent(template.emailContent || template.content || "");
      setEditedSmsContent(template.smsContent || "");

      // Fetch fresh data from the server if dialog is open
      if (isOpen) {
        fetchTemplateDetails(template.id);
      }
    }
  }, [template, isOpen]);

  const handleSave = () => {
    if (template) {
      const updatedTemplate = {
        ...template,
        subject: editedSubject,
        emailContent: editedContent,
        smsContent: editedSmsContent,
        designJson: undefined,
      };

      onSave(updatedTemplate);
      onOpenChange(false);
    }
  };

  if (!template) return null;

  // Get variables for this template type (still useful for display)
  const variables = templateVariables[template.category] || [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1200px] h-[80vh]">
        <DialogHeader>
          <DialogTitle>Edit Email Template: {template.name}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Loading template details...</span>
          </div>
        ) : (
          <div className="space-y-4 h-full overflow-y-auto">
            <div>
              <label htmlFor="subject" className="text-sm font-medium">
                Subject Line
              </label>
              <Textarea
                id="subject"
                value={editedSubject}
                onChange={(e) => setEditedSubject(e.target.value)}
                className="mt-1"
                placeholder="Email subject line"
              />
            </div>

            <div className="flex-grow flex flex-col">
              <label className="text-sm font-medium">Email Content</label>
              <EmailTemplateEditor
                content={editedContent}
                onChange={(htmlContent) => {
                  setEditedContent(htmlContent);
                }}
                variables={variables}
                template={template}
              />
            </div>

            <div>
              <label htmlFor="smsContent" className="text-sm font-medium">
                SMS Content
              </label>
              <Textarea
                id="smsContent"
                value={editedSmsContent}
                onChange={(e) => setEditedSmsContent(e.target.value)}
                className="mt-1"
                placeholder="SMS message content"
                rows={4}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Plain text message. You can use placeholders like [Employee
                Name], [Event Name], [Event Date], and [Event Location].
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
